import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SmilePlus } from 'lucide-react'
import { ReactionPicker } from './ReactionPicker'
import { useEntityReactions } from './useEntityReactions'
import type { ReactNode } from 'react'
import type { ReactionGroup } from './useEntityReactions'
import { useMembers } from '@/lib/hooks/useWorkspaceUsers'
import useLoginStore from '@/store/LoginStore'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface ReactionPickerPopoverProps {
  onSelect: (emoji: string, label?: string) => void
  children: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  align?: 'start' | 'center' | 'end'
}

/** A trigger that opens the reaction picker and closes it on pick. */
export function ReactionPickerPopover({
  onSelect,
  children,
  open: controlledOpen,
  onOpenChange,
  align = 'start',
}: ReactionPickerPopoverProps) {
  const [ownOpen, setOwnOpen] = useState(false)
  const open = controlledOpen ?? ownOpen
  const setOpen = (next: boolean) => {
    setOwnOpen(next)
    onOpenChange?.(next)
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} className="w-auto overflow-hidden rounded-xl p-0">
        <ReactionPicker
          onSelect={(emoji, label) => {
            onSelect(emoji, label)
            setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

interface ReactionPillsProps {
  entityType: string
  entityId: string
  envId?: string
  /**
   * `hover`: the ☺ after the pills shows only while the parent `group` is
   * hovered, and nothing renders with no reactions (comments, design 35a).
   * `always`: the ☺ is the entry point and stays visible.
   */
  addButton?: 'hover' | 'always'
  className?: string
}

/**
 * Reaction pills (design 35). Yours take the terracotta tint; others sit on a
 * borderless paper tint that picks up a border on hover. Hovering a pill
 * names who reacted; clicking adds or removes yours.
 */
export function ReactionPills({
  entityType,
  entityId,
  envId,
  addButton = 'hover',
  className,
}: ReactionPillsProps) {
  const { groups, toggle } = useEntityReactions(entityType, entityId, envId)
  return <ReactionPillList groups={groups} onToggle={toggle} addButton={addButton} className={className} />
}

interface ReactionPillListProps {
  groups: Array<ReactionGroup>
  onToggle: (emoji: string, label?: string) => void
  addButton?: 'hover' | 'always'
  className?: string
}

/** The pills alone, for hosts that already hold the reactions (a comment row). */
export function ReactionPillList({ groups, onToggle, addButton = 'hover', className }: ReactionPillListProps) {
  const { t } = useTranslation('common')
  const [pickerOpen, setPickerOpen] = useState(false)

  if (groups.length === 0 && addButton === 'hover') return null

  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {groups.map((group) => (
        <ReactionPill key={group.emoji} group={group} onToggle={() => onToggle(group.emoji)} />
      ))}
      <ReactionPickerPopover onSelect={onToggle} open={pickerOpen} onOpenChange={setPickerOpen}>
        <button
          type="button"
          aria-label={t('reactions.addReaction')}
          className={cn(
            'grid size-6 place-items-center rounded-full text-muted-foreground transition-opacity hover:bg-background hover:text-foreground focus-visible:opacity-100',
            addButton === 'hover' &&
              !pickerOpen &&
              'opacity-0 group-hover/comment:bg-background group-hover/comment:opacity-100 [@media(hover:none)]:opacity-100',
          )}
        >
          <SmilePlus className="size-[13px]" />
        </button>
      </ReactionPickerPopover>
    </div>
  )
}

function ReactionPill({ group, onToggle }: { group: ReactionGroup; onToggle: () => void }) {
  const { t, i18n } = useTranslation('common')
  const { users } = useMembers()
  const currentUserId = useLoginStore((state) => state.userId)

  // "You, Sam Kim and Rae Park": you first, then the others in reaction order.
  const names = useMemo(() => {
    const byId = new Map(users.map((member) => [member.userId, member]))
    const list = group.mine ? [t('reactions.you')] : []
    for (const id of group.userIds) {
      if (id === currentUserId) continue
      const member = byId.get(id)
      list.push(member?.name || member?.email || t('reactions.someone'))
    }
    try {
      return new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(list)
    } catch {
      return list.join(', ')
    }
  }, [users, group, currentUserId, i18n.language, t])

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-pressed={group.mine}
          onClick={onToggle}
          className={cn(
            'flex h-6 items-center gap-[5px] rounded-full pl-1.5 pr-2 text-xs transition-shadow',
            group.mine
              ? 'bg-brand-tint font-semibold text-brand-tint-foreground shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--brand)_35%,transparent)]'
              : 'bg-background text-secondary-foreground hover:shadow-[inset_0_0_0_1px_var(--input)]',
          )}
        >
          <span className="text-sm leading-none">{group.emoji}</span>
          <span className="font-mono text-[11px] tabular-nums">{group.count}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px] rounded-lg px-2.5 py-[7px] text-left text-xs leading-[1.45]">
        {t('reactions.reactedWith', { names })} <span className="text-[13px]">{group.emoji}</span>
        <div className="mt-0.5 opacity-70">
          {group.mine ? t('reactions.clickToRemove') : t('reactions.clickToAdd')}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
